import { useEffect } from 'react'

/**
 * Keyboard shortcut handler for the DXF Nester page.
 *
 *   1..9     — switch tab by index
 *   g        — run nest
 *   ⌘S / Ctrl+S — save project
 *   ⌘E / Ctrl+E — export current nest CSV
 *   n        — new project (reset form)
 *   ?        — show keymap overlay
 *   Esc      — clear highlighted part / close overlay
 */
export default function useNesterKeyboard({
  tabs,
  setActiveTab,
  onRunNest,
  onSave,
  onNewProject,
  onClearHighlight,
  onToggleKeymap,
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return

    function isTyping(e) {
      const tag = (e.target.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
      if (e.target.isContentEditable) return true
      return false
    }

    function handler(e) {
      const isMod = e.metaKey || e.ctrlKey

      // Cmd/Ctrl-keyed — always active even when typing in a text field
      if (isMod && (e.key === 's' || e.key === 'S')) {
        e.preventDefault()
        onSave?.()
        return
      }
      if (isMod && (e.key === 'e' || e.key === 'E')) {
        // let the browser do its thing inside inputs
        if (isTyping(e)) return
        e.preventDefault()
        // handled elsewhere — future use
        return
      }

      if (isTyping(e)) return
      if (isMod) return

      // Tab shortcuts 1..9
      if (/^[1-9]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1
        if (tabs && tabs[idx]) {
          setActiveTab(tabs[idx].id)
          e.preventDefault()
        }
        return
      }

      if (e.key === 'g' || e.key === 'G') {
        e.preventDefault()
        onRunNest?.()
        return
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault()
        onNewProject?.()
        return
      }
      if (e.key === '?') {
        e.preventDefault()
        onToggleKeymap?.()
        return
      }
      if (e.key === 'Escape') {
        onClearHighlight?.()
        return
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [tabs, setActiveTab, onRunNest, onSave, onNewProject, onClearHighlight, onToggleKeymap, enabled])
}

export const KEYMAP = [
  { keys: ['1', '…', '9'], action: 'Switch tab' },
  { keys: ['G'], action: 'Run nest' },
  { keys: ['⌘', 'S'], action: 'Save project' },
  { keys: ['N'], action: 'New project' },
  { keys: ['Esc'], action: 'Clear selection' },
  { keys: ['?'], action: 'Show this help' },
]
